import { useState, useCallback } from "react";
import { trpc } from "../../../lib/api/trpc";

// Define types for bulk operations
interface BulkUser {
  firstName: string;
  lastName: string;
  email: string;
}

interface BulkOperationProgress {
  current: number;
  total: number;
  status: "idle" | "processing" | "completed" | "cancelled";
  message?: string;
}

interface BulkOperationResult<T = any> {
  successful: T[];
  failed: Array<{ item: T; error: string }>;
  total: number;
}

/**
 * Hook for handling bulk user operations
 */
export const useBulkOperations = () => {
  const [progress, setProgress] = useState<BulkOperationProgress>({
    current: 0,
    total: 0,
    status: "idle",
  });

  // Get the trpc context for revalidating queries
  // @ts-ignore - Type checking for trpc.useContext
  const utils = trpc.useContext();

  // Create mutations at the top level
  // @ts-ignore - Type checking for trpc.user property
  const createUserMutation = trpc.user.createUser.useMutation();
  // @ts-ignore - Type checking for trpc.user property
  const updateUserMutation = trpc.user.updateUser.useMutation();
  // @ts-ignore - Type checking for trpc.user property
  const sendOnboardingMutation = trpc.user.sendOnboardingEmail.useMutation();
  // @ts-ignore - Type checking for trpc.user property
  const deleteUserMutation = trpc.user.deleteUser.useMutation();
  // @ts-ignore - Type checking for trpc.user property
  const archiveUserMutation = trpc.user.archiveUser.useMutation();

  // Helper function to process items in batches
  const processBatch = async <T, R>(
    items: T[],
    batchSize: number,
    processor: (item: T) => Promise<R>,
    onProgress?: (current: number, total: number) => void,
  ): Promise<BulkOperationResult<T>> => {
    const results: BulkOperationResult<T> = {
      successful: [],
      failed: [],
      total: items.length,
    };

    // Process items in batches
    for (let i = 0; i < items.length; i += batchSize) {
      const batch = items.slice(i, i + batchSize);

      // Process batch items concurrently
      const batchPromises = batch.map(async (item) => {
        try {
          await processor(item);
          results.successful.push(item);
        } catch (error: any) {
          results.failed.push({
            item,
            error: error?.message || "Unknown error",
          });
        }
      });

      // Wait for all items in the batch to complete
      await Promise.all(batchPromises);

      // Update progress
      const processed = Math.min(i + batchSize, items.length);
      if (onProgress) {
        onProgress(processed, items.length);
      }
    }

    return results;
  };

  // Helper function to generate a random password
  const generateRandomPassword = (length: number = 12): string => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  /**
   * Bulk create users from CSV data
   */
  const bulkCreateUsers = useCallback(
    async (users: BulkUser[]) => {
      setProgress({
        current: 0,
        total: users.length,
        status: "processing",
        message: "Creating users...",
      });

      try {
        const result = await processBatch(
          users,
          5, // Process 5 users concurrently
          async (user) => {
            // Generate a random password for each user
            const password = generateRandomPassword();

            // Create user with default role and status
            const userData = {
              ...user,
              name: `${user.firstName} ${user.lastName}`,
              password, // Add the generated password
              role: "USER" as const,
              status: "CREATED" as const,
              sendInviteEmail: false, // Don't send emails during bulk import
            };

            return await createUserMutation.mutateAsync(userData);
          },
          (current, total) => {
            setProgress({
              current,
              total,
              status: "processing",
              message: `Creating user ${current} of ${total}...`,
            });
          },
        );

        setProgress({
          current: users.length,
          total: users.length,
          status: "completed",
          message: `Completed: ${result.successful.length} created, ${result.failed.length} failed`,
        });

        // Invalidate the users list query to refresh data
        // @ts-ignore - Type checking for utils.user property
        await utils.user.getUsers.invalidate();

        return result;
      } catch (error: any) {
        setProgress({
          current: 0,
          total: users.length,
          status: "idle",
          message: "Bulk creation failed",
        });
        throw error;
      }
    },
    [utils, createUserMutation],
  );

  /**
   * Bulk update user status
   */
  const bulkUpdateUserStatus = useCallback(
    async (userIds: string[], status: string) => {
      setProgress({
        current: 0,
        total: userIds.length,
        status: "processing",
        message: "Updating user statuses...",
      });

      try {
        const result = await processBatch(
          userIds,
          5, // Process 5 users concurrently
          async (userId) => {
            return await updateUserMutation.mutateAsync({
              id: userId,
              accountStatus: status,
            });
          },
          (current, total) => {
            setProgress({
              current,
              total,
              status: "processing",
              message: `Updating user ${current} of ${total}...`,
            });
          },
        );

        setProgress({
          current: userIds.length,
          total: userIds.length,
          status: "completed",
          message: `Completed: ${result.successful.length} updated, ${result.failed.length} failed`,
        });

        // Invalidate the users list query to refresh data
        // @ts-ignore - Type checking for utils.user property
        await utils.user.getUsers.invalidate();

        return result;
      } catch (error: any) {
        setProgress({
          current: 0,
          total: userIds.length,
          status: "idle",
          message: "Bulk update failed",
        });
        throw error;
      }
    },
    [utils, updateUserMutation],
  );

  /**
   * Bulk send onboarding emails to CREATED or ONBOARDING status users
   */
  const bulkSendOnboardingEmails = useCallback(
    async (userIds: string[]) => {
      setProgress({
        current: 0,
        total: userIds.length,
        status: "processing",
        message: "Sending onboarding emails...",
      });

      try {
        const result = await processBatch(
          userIds,
          5, // Process 5 users concurrently
          async (userId) => {
            return await sendOnboardingMutation.mutateAsync({ userId });
          },
          (current, total) => {
            setProgress({
              current,
              total,
              status: "processing",
              message: `Sending email ${current} of ${total}...`,
            });
          },
        );

        setProgress({
          current: userIds.length,
          total: userIds.length,
          status: "completed",
          message: `Completed: ${result.successful.length} sent, ${result.failed.length} failed`,
        });

        return result;
      } catch (error: any) {
        setProgress({
          current: 0,
          total: userIds.length,
          status: "idle",
          message: "Bulk email send failed",
        });
        throw error;
      }
    },
    [sendOnboardingMutation],
  );

  /**
   * Bulk archive users
   */
  const bulkArchiveUsers = useCallback(
    async (userIds: string[]) => {
      setProgress({
        current: 0,
        total: userIds.length,
        status: "processing",
        message: "Archiving users...",
      });

      try {
        const result = await processBatch(
          userIds,
          5, // Process 5 users concurrently
          async (userId) => {
            return await archiveUserMutation.mutateAsync({ userId });
          },
          (current, total) => {
            setProgress({
              current,
              total,
              status: "processing",
              message: `Archiving user ${current} of ${total}...`,
            });
          },
        );

        setProgress({
          current: userIds.length,
          total: userIds.length,
          status: "completed",
          message: `Completed: ${result.successful.length} archived, ${result.failed.length} failed`,
        });

        // Invalidate the users list query to refresh data
        // @ts-ignore - Type checking for utils.user property
        await utils.user.getUsers.invalidate();

        return result;
      } catch (error: any) {
        setProgress({
          current: 0,
          total: userIds.length,
          status: "idle",
          message: "Bulk archive failed",
        });
        throw error;
      }
    },
    [utils, archiveUserMutation],
  );

  /**
   * Bulk delete users
   */
  const bulkDeleteUsers = useCallback(
    async (userIds: string[]) => {
      setProgress({
        current: 0,
        total: userIds.length,
        status: "processing",
        message: "Deleting users...",
      });

      try {
        const result = await processBatch(
          userIds,
          5, // Process 5 users concurrently
          async (userId) => {
            return await deleteUserMutation.mutateAsync({ id: userId });
          },
          (current, total) => {
            setProgress({
              current,
              total,
              status: "processing",
              message: `Deleting user ${current} of ${total}...`,
            });
          },
        );

        setProgress({
          current: userIds.length,
          total: userIds.length,
          status: "completed",
          message: `Completed: ${result.successful.length} deleted, ${result.failed.length} failed`,
        });

        // Invalidate the users list query to refresh data
        // @ts-ignore - Type checking for utils.user property
        await utils.user.getUsers.invalidate();

        return result;
      } catch (error: any) {
        setProgress({
          current: 0,
          total: userIds.length,
          status: "idle",
          message: "Bulk deletion failed",
        });
        throw error;
      }
    },
    [utils, deleteUserMutation],
  );

  /**
   * Cancel the current bulk operation
   */
  const cancelOperation = useCallback(() => {
    setProgress({
      current: progress.current,
      total: progress.total,
      status: "cancelled",
      message: "Operation cancelled",
    });
  }, [progress]);

  /**
   * Reset progress state
   */
  const resetProgress = useCallback(() => {
    setProgress({
      current: 0,
      total: 0,
      status: "idle",
    });
  }, []);

  return {
    // Operations
    bulkCreateUsers,
    bulkUpdateUserStatus,
    bulkSendOnboardingEmails,
    bulkArchiveUsers,
    bulkDeleteUsers,

    // Progress tracking
    progress,
    cancelOperation,
    resetProgress,
  };
};

// Export types for use in components
export type { BulkUser, BulkOperationProgress, BulkOperationResult };
